import { createSelector } from '@reduxjs/toolkit';
import type { AppUserInfo } from '@/models/AppViewModel';
import type { UserTag } from '@/store/userTag';
import { usersApi } from '@/store/usersApi';
import type { ViewingTagState } from '@/store/viewingTagSlice';
import { viewingTagSelectionIsListed } from '@/utils/accountInvestorViewingOptions';
import { userHasTagSelectorAccess } from '@/utils/tagSelectorAccess';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';

export interface QueryViewingTagReady {
  ready: true;
  selection: ViewingTagSelection;
}

export interface QueryViewingTagPending {
  ready: false;
}

export type QueryViewingTag = QueryViewingTagReady | QueryViewingTagPending;

interface QueryViewingTagState {
  appView: { UserInfo?: AppUserInfo };
  viewingTag: Pick<ViewingTagState, 'selectedViewingTag'>;
  usersApi?: ReturnType<typeof usersApi.reducer>;
}

type UserTagsQueryState = Parameters<
  ReturnType<typeof usersApi.endpoints.getUserTags.select>
>[0];

const NO_SELECTION: QueryViewingTagReady = { ready: true, selection: null };
const PENDING: QueryViewingTagPending = { ready: false };

/**
 * Distinguishes a store that never registered `usersApi` from a registered
 * cache that simply has no tag list yet. Those two cases return different
 * selections, so they cannot share an `undefined` input.
 */
const TAG_CACHE_ABSENT: unique symbol = Symbol('viewing-tag-cache-absent');

type TagListInput = UserTag[] | undefined | typeof TAG_CACHE_ABSENT;

const selectUserTagsQuery = usersApi.endpoints.getUserTags.select(undefined);

const selectHasTagSelectorAccess = (state: QueryViewingTagState): boolean =>
  userHasTagSelectorAccess(state.appView.UserInfo);

const selectSelectedViewingTag = (
  state: QueryViewingTagState,
): ViewingTagSelection => state.viewingTag.selectedViewingTag ?? null;

const selectTagList = (state: QueryViewingTagState): TagListInput => {
  if (state.usersApi === undefined) {
    return TAG_CACHE_ABSENT;
  }
  return selectUserTagsQuery(state as UserTagsQueryState).data;
};

const selectTagsLoading = (state: QueryViewingTagState): boolean => {
  if (state.usersApi === undefined) {
    return false;
  }
  return selectUserTagsQuery(state as UserTagsQueryState).isLoading;
};

function readyWithSelection(
  selection: ViewingTagSelection,
): QueryViewingTagReady {
  if (selection === null) {
    return NO_SELECTION;
  }
  return { ready: true, selection };
}

/**
 * Selection safe to send as a household filter.
 * A tag list already in hand is used, including one kept after a failed
 * refetch. Household queries wait only while the first tag request is in
 * flight and a stored tag still needs to be checked against that list.
 * All accounts has no stored tag to validate, so it stays ready while the
 * catalog loads. A failure, or a list that was never requested, omits the
 * stored id so those screens still load. Stores that do not register
 * `usersApi` keep the raw selection so existing query tests stay on the
 * previous merge path.
 *
 * The result object is memoized on access, the stored selection, the tag
 * list, and whether that list is loading. Account, holding, transaction,
 * document, and diversification wrappers pass this straight to `useSelector`,
 * so an unrelated dispatch must not hand them a new object.
 */
export const selectQueryViewingTag = createSelector(
  [
    selectHasTagSelectorAccess,
    selectSelectedViewingTag,
    selectTagList,
    selectTagsLoading,
  ],
  (hasAccess, selection, tagList, tagsLoading): QueryViewingTag => {
    if (!hasAccess || selection === null) {
      return NO_SELECTION;
    }
    if (tagList === TAG_CACHE_ABSENT) {
      return readyWithSelection(selection);
    }
    if (tagList) {
      if (!viewingTagSelectionIsListed(selection, tagList)) {
        return NO_SELECTION;
      }
      return readyWithSelection(selection);
    }
    if (tagsLoading) {
      return PENDING;
    }
    return NO_SELECTION;
  },
);

/** Tag id to merge into a request. An unconfirmed selection contributes no filter. */
export function confirmedViewingTagSelection(
  state: Parameters<typeof selectQueryViewingTag>[0],
): ViewingTagSelection {
  const confirmed = selectQueryViewingTag(state);
  return confirmed.ready ? confirmed.selection : null;
}
